import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Popover,
  InteractiveContainer,
  PlainButton,
  Button,
  TextInput,
  Tooltip,
  NativeSelectUnstyled,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useInvoiceFormOptions } from "@jugl-web/domain-resources/invoices/hooks/useInvoiceFormOptions";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { ReactComponent as PenIcon } from "./assets/pen.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

interface FormFields {
  value: number;
}

export const InvoiceLevelDiscount = () => {
  const { t } = useTranslations();
  const {
    formValues,
    setInvoiceLevelDiscount,
    invoiceLevelDiscount,
    itemsField,
  } = useInvoicesItemFormPageContext();
  const [type, setType] = useState<string>(invoiceLevelDiscount.type);

  const subtotal = itemsField.fields.reduce(
    (acc, item) => acc + Number(item.price) * Number(item.qty || 0),
    0
  );

  const schema = yup.object().shape({
    value: yup
      .number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .min(0)
      .test("max-value", (value) => {
        if (!value) return true;

        if (type === "percentage") {
          return value <= 100;
        }

        return value <= subtotal;
      }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue: setFormValue,
    trigger,
  } = useForm<FormFields>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      value: invoiceLevelDiscount.amount || 0,
    },
  });

  const selectedCurrency = formValues.currency;
  const { discountUnits } = useInvoiceFormOptions({
    currency: selectedCurrency,
  });

  const onSave = (data: FormFields) => {
    setInvoiceLevelDiscount({
      type,
      amount: data.value,
    });
  };

  const resetForm = useCallback(() => {
    setType(invoiceLevelDiscount.type);
    setFormValue("value", invoiceLevelDiscount.amount || 0);
  }, [invoiceLevelDiscount, setFormValue]);

  useEffect(() => {
    resetForm();
  }, [invoiceLevelDiscount, resetForm]);

  useEffect(() => {
    // Revalidate form when type changes
    trigger("value");
  }, [type, trigger]);

  return (
    <Popover
      placement="right-start"
      className="p-6"
      renderTrigger={({ Trigger, triggerRef }) => (
        <Trigger ref={triggerRef} as={InteractiveContainer} className="w-10">
          <Tooltip
            delay="default"
            placement="right"
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="-my-3 flex h-10 w-10 items-center justify-center rounded-lg border border-solid border-grey-100"
                ref={ref}
                {...props}
              >
                <PenIcon />
              </PlainButton>
            )}
          >
            {t({
              defaultMessage: "Edit",
              id: "common.edit",
            })}
          </Tooltip>
        </Trigger>
      )}
    >
      {({ onClose }) => (
        <div className="flex flex-col gap-4">
          <span className="flex items-center justify-between font-semibold text-[#383838]">
            <div>
              {t({
                id: "invoices.invoice-discount-title",
                defaultMessage: "Add discount",
              })}
            </div>
            <PlainButton onClick={onClose}>
              <CloseIcon />
            </PlainButton>
          </span>
          <form
            onSubmit={handleSubmit((data) => {
              onSave(data);
              onClose();
            })}
            className="flex w-screen max-w-sm flex-col gap-5"
          >
            <TextInput
              type="number"
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              label={t({
                id: "invoices.invoice-discount-percentage",
                defaultMessage: "Discount",
              })}
              isRequired
              errorMessage={errors.value?.message}
              {...register("value", {
                valueAsNumber: true,
              })}
              endContent={
                <NativeSelectUnstyled
                  className="w-16 font-secondary text-base text-dark"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                  }}
                >
                  {discountUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.value}
                    </option>
                  ))}
                </NativeSelectUnstyled>
              }
            />

            <div className="mt-3 grid grid-cols-2 gap-3">
              <Button
                variant="tertiary"
                className="h-10 w-full shrink"
                onClick={onClose}
                type="button"
              >
                {t({
                  id: "common.close",
                  defaultMessage: "Close",
                })}
              </Button>
              <Button className="h-10 w-full shrink" type="submit">
                {t({
                  id: "common.save",
                  defaultMessage: "Save",
                })}
              </Button>
            </div>
          </form>
        </div>
      )}
    </Popover>
  );
};
